import React, { useState, useEffect } from 'react';
import P from 'prop-types';
import { object, string } from 'yup';
import { graphql } from 'react-apollo';
import { compose } from 'recompose';
import styled from 'styled-components';
import NumberFormat from 'react-number-format';
import config from 'config';

import { VERIFY_MFA_MUTATION } from 'gql/mutations';
import { Formik } from 'formik';
import Button from 'components/Button/';
import Icon from 'components/Icon/';
import { ensureLoggedOut } from 'utils/ensureLoggedOut';
import ERROR_CODES from 'data/errorCodes';
import colors from '../../../config/theme/colors';

const schema = object().shape({
  verificationCode: string()
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(6, 'Must be exactly 6 digits')
    .max(6, 'Must be exactly 6 digits')
    .required(),
});

const verificationMessages = {
  CODE_SENT: () => (
    <StyledVerificationMessageSuccess>
      <Icon
        name="circleTick"
        className="ml-2"
        size={Icon.SIZE.xs}
        color={colors.gulfstream}
      />
      <div style={{ marginLeft: '5px' }}>Your code has been sent!</div>
    </StyledVerificationMessageSuccess>
  ),
  CODE_MISMATCH: () => (
    <StyledVerificationMessageError>
      The code you entered doesn’t match the one sent to your phone. Please
      check the code and try again.
    </StyledVerificationMessageError>
  ),
  VERIFICATION_FAILED: () => (
    <StyledVerificationMessageError>
      Verification failed. Click the link below to try logging in again.
    </StyledVerificationMessageError>
  ),
  EMPTY_MESSAGE: () => '',
};

// NOTE: This is for users who activated SMS MFA
const MfaVerification = ({
  email,
  session,
  phoneNumber,
  verifyMFAMutation,
  popView,
  client,
}) => {
  const [verificationInfo, setVerificationInfo] = useState({
    isFailed: false,
    message: verificationMessages.CODE_SENT,
  });

  useEffect(() => {
    setTimeout(function() {
      setVerificationInfo({
        isFailed: false,
        message: verificationMessages.EMPTY_MESSAGE,
      });
    }, 5000);
  }, []);

  return (
    <Wrapper>
      <Heading>Enter the verification code</Heading>
      <StyledDescription>
        To complete your login, please enter the verification code sent to:{' '}
        <strong>{phoneNumber}</strong>
      </StyledDescription>
      <Formik
        initialValues={{ verificationCode: '' }}
        validationSchema={schema}
        onSubmit={async ({ verificationCode }, { setSubmitting }) => {
          try {
            const { data } = await verifyMFAMutation({
              variables: {
                input: {
                  email,
                  verificationCode,
                  session,
                },
              },
            });
            if (!data) throw new Error('no data');

            localStorage.setItem(
              'accessToken',
              data.mfaVerification.session.id,
            );
            await client.resetStore();
            window.location.href = `${config.WEB_URL}/management-portal`;
          } catch (err) {
            const [error] = err.graphQLErrors;
            if (error.code === ERROR_CODES.MFA_CODE_INVALID) {
              setVerificationInfo({
                isFailed: true,
                message: verificationMessages.CODE_MISMATCH,
              });
            } else {
              setVerificationInfo({
                isFailed: true,
                message: verificationMessages.VERIFICATION_FAILED,
              });
            }
          }
          setSubmitting(false);
        }}
        render={({
          isSubmitting,
          handleSubmit,
          errors,
          touched,
          setFieldValue,
          setFieldTouched,
          values,
        }) => (
          <StyledFormContainer className="formik">
            <StyledFormGroup>
              <label htmlFor="verification-code">Verification code</label>
              <StyledNumberFormat
                type="text"
                id="verification-code"
                isNumericString
                hasError={
                  verificationInfo.isFailed ||
                  (touched.verificationCode && !!errors.verificationCode)
                }
                value={values.verificationCode}
                onValueChange={(e) => {
                  setFieldValue('verificationCode', e.value);
                  setFieldTouched('verificationCode', true);
                }}
                disabled={isSubmitting}
                format="# # # # # #"
                mask="_"
                allowEmptyFormatting
                autoFocus
              />
              {verificationInfo.message()}
            </StyledFormGroup>

            <div>
              <StyledSubmitButton
                testAttr="VerifyButton"
                disabled={
                  !values.verificationCode ||
                  !!errors.verificationCode ||
                  isSubmitting
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit();
                }}
              >
                {isSubmitting ? 'Verifying...' : 'Verify'}
              </StyledSubmitButton>
            </div>

            <StyledActionText>
              Didn't get a code?{' '}
              <LinkButton
                color="transparent"
                underlineText
                onClick={() => popView()}
              >
                Click here to log in again
              </LinkButton>
            </StyledActionText>

            <StyledActionText>
              Having issues?{' '}
              <Link href="mailto:help@becollective.com">Contact us</Link> for
              support.
            </StyledActionText>
          </StyledFormContainer>
        )}
      />
    </Wrapper>
  );
};

MfaVerification.propTypes = {
  email: P.string.isRequired,
  session: P.string.isRequired,
  phoneNumber: P.string.isRequired,
  verifyMFAMutation: P.func.isRequired,
  client: P.shape({
    resetStore: P.func.isRequired,
  }).isRequired,
  popView: P.func,
};

export default compose(
  graphql(VERIFY_MFA_MUTATION, { name: 'verifyMFAMutation' }),
  ensureLoggedOut,
)(MfaVerification);

// ==================== Start styled components ==================
const Wrapper = styled.div`
  width: 530px;
  margin-top: 140px;
  padding: 20px 30px;
  border-radius: 4px;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.2);
  background-color: #f7f7f7;
`;
const Heading = styled.h2`
  text-align: left;
  font-size: 24px;
  font-weight: bold;
`;
const StyledNumberFormat = styled(NumberFormat)(
  ({ theme: { colors }, hasError }) => `
  width: 100%;
  border-radius: 4px;
  border: 1px solid ${hasError ? colors.roman : '#d1d1d1'};
  outline: none;
  font-size: 14px;
  color: #404040;
  line-height: 1.25rem;
  height: 40px;
  text-align: center !important;
  margin-bottom: 0.25rem !important;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  :focus {
    border: 1px solid #81bba8;
  }
`,
);
const LinkButton = styled(Button)(
  ({ theme: { colors } }) => `
  padding: 0px;
  color: ${colors.gulfstream} !important;
  font-size: 14px;
`,
);
const Link = styled.a(
  ({ theme: { colors } }) => `
  padding: 0px;
  color: ${colors.gulfstream} !important;
  font-size: 14px;
`,
);
const StyledDescription = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
`;
const StyledFormContainer = styled.div`
  width: 100%;
`;
const StyledFormGroup = styled.div`
  position: relative;
  margin-bottom: 62px;
  label {
    font-weight: 900;
    margin-bottom: 5px;
    display: block;
  }
`;
const StyledVerificationMessageSuccess = styled.div(
  ({ theme: { colors } }) => `
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: ${colors.gulfstream};
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);
const StyledVerificationMessageError = styled.div(
  ({ theme: { colors } }) => `
    font-size: 14px;
    line-height: 19px;
    color: ${colors.roman};
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
);
const StyledActionText = styled.div(
  ({ theme: { colors } }) => `
    font-weight: 400;
    font-size: 14px;
    line-height: 19px;
    color: ${colors.tundora};
    margin-top: 20px;

    button {
      text-decoration: underline;
      color: ${colors.gulfstream};
    }

    & > div {
      display: inline-block;
    }
  `,
);
const StyledSubmitButton = styled(Button)`
  width: 160px;
  height: 40px;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
// ==================== End styled components ==================
